import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminFetch } from '../../lib/adminFetch';

/* =========================================================================
 * 地域別最低賃金（時間額）
 *   都道府県別の最低賃金を年度ごとに保存。MHLWの「全国一覧」PDFを
 *   Claude API（サーバー経由）で読み取り、表に流し込める。
 *   適正賃金分析の基準線に使用。都道府県名は地域指数（別添3）と同じ短縮表記。
 * ========================================================================= */

export const DEFAULT_MIN_WAGE = [
  { name: '北海道', wage: 1075 }, { name: '青森', wage: 1029 }, { name: '岩手', wage: 1031 }, { name: '宮城', wage: 1038 },
  { name: '秋田', wage: 1031 }, { name: '山形', wage: 1032 }, { name: '福島', wage: 1033 }, { name: '茨城', wage: 1074 },
  { name: '栃木', wage: 1068 }, { name: '群馬', wage: 1063 }, { name: '埼玉', wage: 1141 }, { name: '千葉', wage: 1140 },
  { name: '東京', wage: 1226 }, { name: '神奈川', wage: 1225 }, { name: '新潟', wage: 1050 }, { name: '富山', wage: 1062 },
  { name: '石川', wage: 1054 }, { name: '福井', wage: 1053 }, { name: '山梨', wage: 1052 }, { name: '長野', wage: 1061 },
  { name: '岐阜', wage: 1065 }, { name: '静岡', wage: 1097 }, { name: '愛知', wage: 1140 }, { name: '三重', wage: 1087 },
  { name: '滋賀', wage: 1080 }, { name: '京都', wage: 1122 }, { name: '大阪', wage: 1177 }, { name: '兵庫', wage: 1116 },
  { name: '奈良', wage: 1051 }, { name: '和歌山', wage: 1045 }, { name: '鳥取', wage: 1030 }, { name: '島根', wage: 1033 },
  { name: '岡山', wage: 1047 }, { name: '広島', wage: 1085 }, { name: '山口', wage: 1043 }, { name: '徳島', wage: 1046 },
  { name: '香川', wage: 1036 }, { name: '愛媛', wage: 1033 }, { name: '高知', wage: 1023 }, { name: '福岡', wage: 1057 },
  { name: '佐賀', wage: 1030 }, { name: '長崎', wage: 1031 }, { name: '熊本', wage: 1034 }, { name: '大分', wage: 1035 },
  { name: '宮崎', wage: 1023 }, { name: '鹿児島', wage: 1026 }, { name: '沖縄', wage: 1023 },
];

const MinWageAdmin = () => {
  const role = localStorage.getItem('admin_role');
  const canEdit = role === 'super_admin' || role === 'system_admin';

  const [years, setYears] = useState([]);
  const [year, setYear] = useState('');
  const [minWage, setMinWage] = useState(DEFAULT_MIN_WAGE);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    adminFetch(`/api/admin-report.php?action=wage_years&t=${Date.now()}`)
      .then((r) => r.json())
      .then((ys) => {
        const arr = Array.isArray(ys) ? ys : [];
        setYears(arr);
        setYear((prev) => prev || (arr.length ? arr[0].year : String(new Date().getFullYear())));
      })
      .catch(() => setYear((prev) => prev || String(new Date().getFullYear())));
  }, []);

  const loadYear = useCallback((y) => {
    if (!y) return;
    adminFetch(`/api/admin-report.php?action=wage_data&year=${encodeURIComponent(y)}&t=${Date.now()}`)
      .then((r) => r.json())
      .then((d) => {
        if (d && Array.isArray(d.minimum) && d.minimum.length) setMinWage(d.minimum);
        else setMinWage(DEFAULT_MIN_WAGE);
      })
      .catch(() => setMinWage(DEFAULT_MIN_WAGE));
  }, []);
  useEffect(() => { if (year) loadYear(year); }, [year, loadYear]);

  const setMW = (i, v) => setMinWage((prev) => prev.map((x, j) => (j === i ? { ...x, wage: v === '' ? '' : Number(v) } : x)));

  const handlePdf = async (file) => {
    if (!file) return; setMsg(''); setPdfLoading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result).split(',')[1] || '');
        fr.onerror = rej; fr.readAsDataURL(file);
      });
      const r = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'min_wage_pdf', pdfBase64: b64 }),
      });
      const d = await r.json();
      if (!d.success) { setMsg(d.message || 'AIによる読み取りに失敗しました'); return; }
      const norm = (s) => { s = String(s || '').trim(); return s === '北海道' ? s : s.replace(/[都府県]$/, ''); };
      const byName = {};
      (d.items || []).forEach((it) => { const w = Number(it.wage); if (it.name && !isNaN(w)) byName[norm(it.name)] = w; });
      let n = 0;
      const next = minWage.map((row) => { const w = byName[row.name]; if (w) { n++; return { ...row, wage: w }; } return row; });
      setMinWage(next);
      setMsg(n > 0 ? `PDFをAIで読み取りました（${n}/47件）。内容を確認して「保存」してください。` : 'PDFから金額を読み取れませんでした。様式をご確認ください。');
    } catch (e) {
      setMsg('PDFの読み取りに失敗しました：' + e.message);
    } finally { setPdfLoading(false); }
  };

  const save = async () => {
    if (!year.trim()) { setMsg('年度を入力してください（例：2026）'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_wage', year: year.trim(), kind: 'minimum', payload: minWage }),
      });
      const d = await res.json();
      setMsg(d.success ? `保存しました（${year.trim()}）` : (d.message || '保存に失敗しました'));
    } catch { setMsg('保存に失敗しました'); }
    setSaving(false);
  };

  if (!canEdit) {
    return <div><h2 className="text-2xl font-bold mb-6">地域別最低賃金</h2>
      <div className="bg-white rounded-xl shadow p-8 text-gray-500">この機能はスーパー／システム管理者のみ利用できます。</div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">地域別最低賃金</h2>
      <p className="text-xs text-gray-500 mb-6">
        都道府県別の最低賃金（時間額）を年度ごとに保存します。MHLWの「地域別最低賃金 全国一覧」PDFをAIで読み取って流し込めます。適正賃金分析の基準線に使用します。
      </p>

      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center flex-wrap gap-3 mb-4">
          <label className="text-sm font-medium text-gray-700">年度</label>
          <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-28 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          {years.length > 0 && (
            <select value="" onChange={(e) => e.target.value && setYear(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 text-xs text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">登録済み年度から選択</option>
              {years.map((y) => <option key={y.year} value={y.year}>{y.year}{y.yearLabel ? `（${y.yearLabel}）` : ''}</option>)}
            </select>
          )}
          {msg && <span className="text-sm text-indigo-700 bg-indigo-50 rounded-lg px-3 py-1">{msg}</span>}
          <button onClick={() => pdfRef.current?.click()} disabled={pdfLoading}
            className="ml-auto bg-rose-50 text-rose-600 px-4 py-2 rounded-lg text-sm font-bold hover:bg-rose-100 disabled:opacity-50">{pdfLoading ? 'AI読み取り中…' : 'PDFから取込（AI）'}</button>
          <input ref={pdfRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => handlePdf(e.target.files[0])} />
          <button onClick={save} disabled={saving}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
            {saving ? '保存中…' : 'この年度に保存'}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2">
          {minWage.map((p, i) => (
            <div key={p.name} className="flex items-center gap-1">
              <span className="text-xs text-gray-600 w-14 shrink-0">{p.name}</span>
              <input type="number" value={p.wage ?? ''} onChange={(e) => setMW(i, e.target.value)}
                className="w-20 text-right border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <span className="text-[10px] text-gray-400">円</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MinWageAdmin;
